<template>
    <AccordionRoot
        v-if="collapsible"
        :as="as"
        :type="type"
        :model-value="model"
        :default-value="fallbackOpen"
        :collapsible="true"
        :unmount-on-hide="false"
        :role="listRole"
        :class="classes"
        @update:model-value="onUpdate"
    >
        <slot />
    </AccordionRoot>

    <Primitive
        v-else
        :as="as"
        :role="listRole"
        :class="classes"
    >
        <slot />
    </Primitive>
</template>

<script setup lang="ts">
import type { KStepsProps } from './types'
import useBem from '@yeeroen/use-bem'
import { AccordionRoot, Primitive } from 'reka-ui'
import { computed, provide } from 'vue'
import { K_STEPS_CONTEXT } from './context'

defineOptions({ name: 'KSteps' })

const props = withDefaults(defineProps<KStepsProps>(), {
    as: 'ol',
    collapsible: false,
    type: 'multiple',
    titleAs: 'h3',
})

defineSlots<{
    /** The steps. Render `KStep`s — nothing else belongs in a list element. */
    default?: () => unknown
}>()

const model = defineModel<string | string[] | undefined>()

const { block, modifier } = useBem('k-steps')

// The visible number is a CSS counter on an `aria-hidden` marker, so the ordinal reaches
// assistive tech through the list semantics instead. Those semantics need defending: Safari
// drops the list role from a list styled `list-style: none`, which is exactly what this
// component does, and `role="list"` puts it back.
const listRole = computed(() => props.as === 'ol' || props.as === 'ul' ? 'list' : undefined)

// A step inside a `div` list must not be an `li`.
const itemAs = computed(() => props.as === 'ol' || props.as === 'ul' ? 'li' as const : 'div' as const)

// reka wants the model shape to match `type`, and silently reinterprets a mismatch. Flag it
// rather than leave a consumer wondering why nothing opens.
if (import.meta.env.DEV) {
    if (props.type === 'multiple' && model.value !== undefined && !Array.isArray(model.value)) {
        console.warn('[KSteps] `type="multiple"` expects an array model.')
    }
    if (props.type === 'single' && Array.isArray(model.value)) {
        console.warn('[KSteps] `type="single"` expects a string model, got an array.')
    }
}

// "Open the first step" is the overwhelmingly common case and it reads better as
// `:default-value="days[0].id"` than as a one-element array, so both shapes are accepted and
// coerced to whatever `type` requires.
const fallbackOpen = computed(() => {
    if (props.defaultValue === undefined) {
        return undefined
    }

    if (props.type === 'multiple') {
        return Array.isArray(props.defaultValue) ? props.defaultValue : [props.defaultValue]
    }

    return Array.isArray(props.defaultValue) ? props.defaultValue[0] : props.defaultValue
})

function onUpdate(next: string | string[] | undefined) {
    model.value = next
}

const classes = computed(() => block(
    modifier('collapsible', props.collapsible),
))

provide(K_STEPS_CONTEXT, {
    collapsible: computed(() => props.collapsible),
    titleAs: computed(() => props.titleAs),
    itemAs,
})
</script>

<style lang="scss">
@use './k-steps.scss';
</style>
